import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import type { Giftcard } from '../types';

const COMMON_AMOUNTS = [10000, 20000, 30000, 50000, 100000];

export function GiftcardsPanel({ advanced }: { advanced: boolean }) {
  const [list, setList] = useState<Giftcard[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    amount: 30000,
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: '',
    expiresAt: '',
    sendEmail: true
  });
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Giftcard['status']>('all');

  async function load() {
    setLoading(true);
    try {
      setList(await api.admin.giftcards());
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setCreating(true);
    setMessage(null);
    try {
      const card = await api.admin.createGiftcard({
        amount: form.amount,
        recipientName: form.recipientName,
        recipientEmail: form.recipientEmail,
        senderName: form.senderName,
        message: form.message,
        expiresAt: form.expiresAt || null,
        sendEmail: form.sendEmail
      });
      setMessage({ tone: 'ok', text: `✓ Giftcard ${card.code} creada${card.emailSentAt ? ' y enviada por email' : ''}.` });
      setForm({ ...form, recipientName: '', recipientEmail: '', senderName: '', message: '', expiresAt: '' });
      await load();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    } finally {
      setCreating(false);
    }
  }

  async function cancel(id: string, code: string) {
    if (!window.confirm(`¿Cancelar giftcard ${code}? El destinatario no podrá usarla más.`)) return;
    await api.admin.cancelGiftcard(id);
    await load();
  }

  async function resend(id: string, code: string) {
    if (!window.confirm(`¿Reenviar email con la giftcard ${code}?`)) return;
    try {
      await api.admin.resendGiftcard(id);
      setMessage({ tone: 'ok', text: `✓ Email reenviado.` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo reenviar' });
    }
  }

  const filtered = useMemo(() => {
    const lower = search.toLowerCase().trim();
    return list.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!lower) return true;
      return c.code.toLowerCase().includes(lower) || c.recipientEmail.toLowerCase().includes(lower) || c.recipientName.toLowerCase().includes(lower);
    });
  }, [list, search, statusFilter]);

  const stats = useMemo(() => {
    const active = list.filter((c) => c.status === 'active');
    return {
      issued: list.length,
      active: active.length,
      outstanding: active.reduce((sum, c) => sum + c.balance, 0),
      redeemed: list.filter((c) => c.status === 'redeemed').length
    };
  }, [list]);

  return (
    <div className="dashboard-stack">
      <section className="admin-card">
        <div className="admin-card-head">
          <div>
            <h2>Giftcards</h2>
            <p className="muted">Genera y envía giftcards. El destinatario las usa al pagar — el saldo se descuenta automáticamente.</p>
          </div>
        </div>

        <div className="admin-grid">
          <article className="metric-card"><span>Emitidas</span><strong>{stats.issued}</strong></article>
          <article className="metric-card"><span>Activas</span><strong>{stats.active}</strong></article>
          <article className="metric-card"><span>Saldo total</span><strong>{formatCLP(stats.outstanding)}</strong></article>
          <article className="metric-card"><span>Canjeadas</span><strong>{stats.redeemed}</strong></article>
        </div>
      </section>

      <form onSubmit={create} className="admin-card">
        <div className="admin-card-head">
          <h2>Nueva giftcard</h2>
          <button className="primary-button compact" disabled={creating}>{creating ? 'Generando...' : 'Crear y enviar'}</button>
        </div>

        <div className="form-grid">
          <label>
            Monto (CLP)
            <input type="number" min={1000} step={1000} value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} required />
          </label>
          <div className="span-2 quick-amount-row">
            {COMMON_AMOUNTS.map((amt) => (
              <button key={amt} type="button" className={classNames('quick-amount', form.amount === amt && 'active')} onClick={() => setForm({ ...form, amount: amt })}>
                {formatCLP(amt)}
              </button>
            ))}
          </div>

          <label>
            Nombre destinatario
            <input value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })} placeholder="Maria Lopez" />
          </label>
          <label>
            Email destinatario
            <input type="email" value={form.recipientEmail} onChange={(e) => setForm({ ...form, recipientEmail: e.target.value })} placeholder="maria@correo.cl" />
          </label>

          <label>
            De parte de (opcional)
            <input value={form.senderName} onChange={(e) => setForm({ ...form, senderName: e.target.value })} placeholder="Pedro" />
          </label>
          {advanced && (
            <label>
              Vence (opcional)
              <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
            </label>
          )}

          <label className="span-2">
            Mensaje personalizado (opcional)
            <textarea rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Feliz cumpleaños! ❤" />
          </label>

          <label className="checkbox-label span-2">
            <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} />
            Enviar email automáticamente al destinatario (necesita Resend configurado)
          </label>
        </div>
        {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
      </form>

      <section className="admin-card">
        <div className="admin-card-head">
          <h2>Emitidas</h2>
          <div className="header-actions-row">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} style={{ width: 'auto' }}>
              <option value="all">Todas</option>
              <option value="active">Activas</option>
              <option value="redeemed">Canjeadas</option>
              <option value="cancelled">Canceladas</option>
              <option value="expired">Expiradas</option>
            </select>
            <input type="search" placeholder="Buscar código o email" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
          </div>
        </div>

        {loading ? (
          <p className="muted">Cargando...</p>
        ) : filtered.length === 0 ? (
          <div className="empty-state subtle"><p>Sin giftcards.</p></div>
        ) : (
          <div className="giftcards-table">
            <div className="giftcards-row giftcards-head">
              <span>Código</span>
              <span>Destinatario</span>
              <span>Saldo</span>
              <span>Estado</span>
              <span></span>
            </div>
            {filtered.map((c) => (
              <div className="giftcards-row" key={c.id}>
                <div>
                  <code>{c.code}</code>
                  <small>{new Date(c.createdAt).toLocaleDateString('es-CL')}</small>
                </div>
                <div>
                  <strong>{c.recipientName || '—'}</strong>
                  <small>{c.recipientEmail || '—'}</small>
                </div>
                <div>
                  <strong>{formatCLP(c.balance)}</strong>
                  <small>de {formatCLP(c.initialAmount)}</small>
                </div>
                <span className={`promo-status status-${c.status}`}>{c.status}</span>
                <div className="giftcard-actions">
                  <button type="button" className="ghost-button compact" onClick={() => api.admin.downloadGiftcardImage(c.id, c.code)}>↓ Imagen</button>
                  {c.recipientEmail && c.status === 'active' && (
                    <button type="button" className="ghost-button compact" onClick={() => resend(c.id, c.code)}>↻ Reenviar</button>
                  )}
                  {c.status === 'active' && (
                    <button type="button" className="ghost-button compact danger" onClick={() => cancel(c.id, c.code)}>Cancelar</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
